import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Calendar, Trash2, Play } from "lucide-react";

const FREQ = ["once", "daily", "weekly", "monthly", "quarterly", "yearly", "custom"];

export default function Scheduler() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", frequency: "daily", cron: "", run_at: "", event_name: "", payload: "{}", is_active: true });

  const load = async () => { const r = await api.get("/scheduled-jobs"); setJobs(r.data); };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const create = async () => {
    try {
      const payload = JSON.parse(form.payload || "{}");
      const body = { ...form, payload };
      if (!body.cron) delete body.cron;
      if (!body.run_at) delete body.run_at;
      if (!body.event_name) delete body.event_name;
      await api.post("/scheduled-jobs", body);
      toast.success("Job scheduled"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete job?")) return;
    try { await api.delete(`/scheduled-jobs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const runNow = async (id) => {
    try { await api.post(`/scheduled-jobs/${id}/run-now`); toast.success("Triggered"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="scheduler-page">
      <PageHeader title="Scheduler" subtitle="Recurring & one-time jobs"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="create-job-button"><Plus className="w-3.5 h-3.5 mr-1.5"/> New job</Button></DialogTrigger>
            <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
              <DialogHeader><DialogTitle className="font-heading">Schedule job</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="job-name-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="job-code-input"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Frequency</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="job-frequency-select"><SelectValue/></SelectTrigger>
                      <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Event to emit</Label><Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} placeholder="MyJobTick" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="job-event-input"/></div>
                </div>
                {form.frequency === "custom" && <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Cron expression</Label><Input value={form.cron} onChange={(e) => setForm({ ...form, cron: e.target.value })} placeholder="*/5 * * * *" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="job-cron-input"/></div>}
                {form.frequency === "once" && <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Run at (ISO datetime)</Label><Input value={form.run_at} onChange={(e) => setForm({ ...form, run_at: e.target.value })} placeholder="2026-03-01T12:00:00Z" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="job-runat-input"/></div>}
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Payload (JSON)</Label><Textarea value={form.payload} onChange={(e) => setForm({ ...form, payload: e.target.value })} rows={3} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono text-xs" data-testid="job-payload-input"/></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-job-button">Schedule</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {jobs.length === 0 ? <EmptyState icon={Calendar} title="No scheduled jobs" subtitle="Create one-time or recurring jobs that emit events on tick."/> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {jobs.map((j) => (
            <Card key={j.id} className="p-5" data-testid={`job-card-${j.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-[15px] text-white font-semibold">{j.name}</span>
                    <Badge tone="info">{j.frequency}</Badge>
                    {j.is_active ? <Badge tone="success">ACTIVE</Badge> : <Badge tone="neutral">INACTIVE</Badge>}
                  </div>
                  <div className="mono text-[11px] text-hocp-textMuted mt-0.5">{j.code}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => runNow(j.id)} className="h-7 w-7 p-0 text-hocp-textMuted hover:text-hocp-deepTeal" data-testid={`run-job-${j.code}`}><Play className="w-3.5 h-3.5"/></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(j.id)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-job-${j.code}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-[11px] mono">
                <div><div className="text-hocp-textMuted uppercase tracking-wider mb-1">Next run</div><div className="text-hocp-textPrimary">{j.next_run ? new Date(j.next_run).toLocaleString() : "—"}</div></div>
                <div><div className="text-hocp-textMuted uppercase tracking-wider mb-1">Last run</div><div className="text-hocp-textPrimary">{j.last_run ? new Date(j.last_run).toLocaleString() : "Never"}</div></div>
                <div><div className="text-hocp-textMuted uppercase tracking-wider mb-1">Runs</div><div className="text-hocp-textPrimary tabular-nums">{j.run_count || 0}</div></div>
                <div><div className="text-hocp-textMuted uppercase tracking-wider mb-1">Event</div><div className="text-hocp-textPrimary">{j.event_name || "—"}</div></div>
              </div>
            </Card>
          ))}
        </div>
      }
    </div>
  );
}
